import { Cell, Pie, PieChart } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";

/**
 * Donut chart for the booking-status breakdown. Center label shows the
 * total. Side legend lists each status with a swatch + count + percent.
 *
 * Color tokens come from the existing `--status-*` CSS vars so the chart
 * matches the StatusBadge palette used everywhere else in the app.
 */

type StatusKey =
  | "pending"
  | "confirmed"
  | "completed"
  | "cancelled"
  | "canceled"
  | "no_show";

const STATUS_ORDER: StatusKey[] = [
  "confirmed",
  "completed",
  "pending",
  "cancelled",
  "canceled",
  "no_show",
];

const STATUS_LABEL: Record<StatusKey, string> = {
  confirmed: "Confirmed",
  completed: "Completed",
  pending: "Pending",
  cancelled: "Cancelled",
  canceled: "Cancelled",
  no_show: "No-show",
};

const STATUS_COLOR: Record<StatusKey, string> = {
  confirmed: "hsl(var(--status-confirmed))",
  completed: "hsl(var(--status-completed))",
  pending: "hsl(var(--status-pending))",
  cancelled: "hsl(var(--status-cancelled))",
  canceled: "hsl(var(--status-cancelled))",
  no_show: "hsl(var(--muted-foreground))",
};

const config = STATUS_ORDER.reduce<ChartConfig>((acc, k) => {
  acc[k] = { label: STATUS_LABEL[k], color: STATUS_COLOR[k] };
  return acc;
}, {});

export function StatusDonut({
  breakdown,
}: {
  breakdown: Record<string, number>;
}) {
  const data = STATUS_ORDER
    .map((k) => ({ name: k, label: STATUS_LABEL[k], value: breakdown[k] ?? 0 }))
    // Collapse the "canceled" / "cancelled" alias into one bucket — backend
    // emits "canceled" but the rest of the app uses "cancelled".
    .reduce<{ name: string; label: string; value: number }[]>((acc, row) => {
      if (row.name === "cancelled" || row.name === "canceled") {
        const existing = acc.find((r) => r.label === "Cancelled");
        if (existing) existing.value += row.value;
        else acc.push({ ...row, label: "Cancelled" });
      } else {
        acc.push(row);
      }
      return acc;
    }, [])
    .filter((d) => d.value > 0);

  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <section className="rounded-xl border bg-card p-5 flex flex-col h-full">
      <header className="mb-4">
        <h2 className="text-base font-semibold tracking-tight">Status breakdown</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Where this window's bookings landed.
        </p>
      </header>

      {total === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground italic">
            No bookings in this window.
          </p>
        </div>
      ) : (
        <div className="flex-1 grid grid-cols-[1fr_auto] items-center gap-6">
          <ChartContainer
            config={config}
            className="aspect-square w-full max-w-[220px] mx-auto"
          >
            <PieChart>
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    hideLabel
                    valueFormatter={(v) =>
                      `${v} (${Math.round((Number(v) / total) * 100)}%)`
                    }
                  />
                }
              />
              <Pie
                data={data}
                dataKey="value"
                nameKey="label"
                innerRadius={56}
                outerRadius={88}
                strokeWidth={2}
                stroke="hsl(var(--card))"
              >
                {data.map((d) => (
                  <Cell
                    key={d.name}
                    fill={STATUS_COLOR[d.name as StatusKey] ?? "hsl(var(--muted))"}
                  />
                ))}
              </Pie>
            </PieChart>
          </ChartContainer>
          <ul className="space-y-2 min-w-[140px]">
            {data.map((d) => {
              const pct = Math.round((d.value / total) * 100);
              return (
                <li
                  key={d.name}
                  className="flex items-center gap-2 text-xs"
                >
                  <span
                    className="h-2.5 w-2.5 rounded-sm shrink-0"
                    style={{
                      background:
                        STATUS_COLOR[d.name as StatusKey] ??
                        "hsl(var(--muted))",
                    }}
                  />
                  <span className="text-muted-foreground">{d.label}</span>
                  <span className="ml-auto tabular-nums font-medium">
                    {d.value}
                  </span>
                  <span className="text-muted-foreground/70 tabular-nums w-8 text-right">
                    {pct}%
                  </span>
                </li>
              );
            })}
            <li className="pt-2 border-t border-border flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">Total</span>
              <span className="ml-auto tabular-nums font-semibold">
                {total}
              </span>
            </li>
          </ul>
        </div>
      )}
    </section>
  );
}
